/**
 * LeftShield Admin - Metrics Page
 * Detailed platform metrics and analytics dashboard
 */
import React, { useState } from 'react';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  tokens,
  TabList,
  Tab,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  DataUsageRegular,
  BuildingRegular,
  DocumentSearchRegular,
  BugRegular,
  ArrowTrendingRegular,
  ClockRegular,
  ServerRegular,
  PeopleRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { detailedMetricsApi, metricsApi } from '../services/api';
import type { TenantActivityMetrics } from '../types';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
  color?: string;
  trend?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, subtitle, color, trend }) => (
  <Card style={{ minWidth: '180px' }}>
    <CardHeader
      image={<div style={{ color: color || tokens.colorBrandForeground1, fontSize: '1.5rem' }}>{icon}</div>}
      header={<Body2>{title}</Body2>}
      description={
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Title2 style={{ color }}>{value}</Title2>
            {trend !== undefined && (
              <Badge
                appearance="tint"
                color={trend >= 0 ? 'success' : 'danger'}
              >
                {trend >= 0 ? '+' : ''}{trend}%
              </Badge>
            )}
          </div>
          {subtitle && (
            <Body2 style={{ color: tokens.colorNeutralForeground3, fontSize: '0.75rem' }}>
              {subtitle}
            </Body2>
          )}
        </div>
      }
    />
  </Card>
);

type TabValue = 'overview' | 'tenants' | 'api';

export const MetricsPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<TabValue>('overview');
  const [timeRange, setTimeRange] = useState('30');

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['platformMetrics'],
    queryFn: () => metricsApi.getPlatformMetrics(),
  });

  const { data: detailedMetrics, isLoading: detailedLoading } = useQuery({
    queryKey: ['detailedMetrics', timeRange],
    queryFn: () => detailedMetricsApi.getDetailedMetrics(parseInt(timeRange)),
  });

  const { data: tenantRankings, isLoading: rankingsLoading } = useQuery({
    queryKey: ['tenantRankings'],
    queryFn: () => detailedMetricsApi.getTenantRankings(20),
  });

  const isLoading = metricsLoading || detailedLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner size="large" label="Loading metrics..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <DataUsageRegular style={{ fontSize: '2rem' }} />
          <Title1>Platform Metrics</Title1>
        </div>
        <Dropdown
          value={`Last ${timeRange} days`}
          selectedOptions={[timeRange]}
          onOptionSelect={(_, data) => setTimeRange(data.optionValue as string)}
          style={{ minWidth: '150px' }}
        >
          <Option value="7">Last 7 days</Option>
          <Option value="30">Last 30 days</Option>
          <Option value="90">Last 90 days</Option>
        </Dropdown>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard
          title="Total Tenants"
          value={metrics?.totalTenants ?? 0}
          icon={<BuildingRegular />}
          subtitle={`${metrics?.activeTenants ?? 0} active`}
        />
        <MetricCard
          title="Total Users"
          value={metrics?.totalUsers ?? 0}
          icon={<PeopleRegular />}
        />
        <MetricCard
          title="Total Scans"
          value={metrics?.totalScans ?? 0}
          icon={<DocumentSearchRegular />}
        />
        <MetricCard
          title="Total Findings"
          value={metrics?.totalFindings ?? 0}
          icon={<BugRegular />}
          color={tokens.colorPaletteRedForeground1}
        />
        <MetricCard
          title="API Requests (Today)"
          value={metrics?.apiRequestsToday ?? 0}
          icon={<ArrowTrendingRegular />}
        />
        <MetricCard
          title="Avg Response Time"
          value={`${metrics?.avgResponseTimeMs ?? 0}ms`}
          icon={<ClockRegular />}
          color={
            (metrics?.avgResponseTimeMs ?? 0) < 200
              ? tokens.colorPaletteGreenForeground1
              : (metrics?.avgResponseTimeMs ?? 0) < 500
              ? tokens.colorPaletteYellowForeground1
              : tokens.colorPaletteRedForeground1
          }
        />
      </div>

      {/* Tabs */}
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
        style={{ marginBottom: '1rem' }}
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="tenants">Tenant Activity</Tab>
        <Tab value="api">API Usage</Tab>
      </TabList>

      {/* Tab Content */}
      {selectedTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Metrics History Chart Placeholder */}
          <Card>
            <CardHeader
              header={<Body1 style={{ fontWeight: 600 }}>Request Volume</Body1>}
              description={<Body2>Daily API requests over time</Body2>}
            />
            <div style={{ padding: '1rem', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {detailedMetrics?.metricsHistory && detailedMetrics.metricsHistory.length > 0 ? (
                <div style={{ width: '100%' }}>
                  {/* Simple bar visualization */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '150px' }}>
                    {detailedMetrics.metricsHistory.slice(-30).map((point, idx) => {
                      const maxVal = Math.max(...detailedMetrics.metricsHistory.map(p => p.value));
                      const height = maxVal > 0 ? (point.value / maxVal) * 100 : 0;
                      return (
                        <div
                          key={idx}
                          style={{
                            flex: 1,
                            height: `${height}%`,
                            backgroundColor: tokens.colorBrandBackground,
                            borderRadius: '2px 2px 0 0',
                            minWidth: '4px',
                          }}
                          title={`${new Date(point.timestamp).toLocaleDateString()}: ${point.value}`}
                        />
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {detailedMetrics.metricsHistory.length > 0 &&
                        new Date(detailedMetrics.metricsHistory[0].timestamp).toLocaleDateString()}
                    </Body2>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {detailedMetrics.metricsHistory.length > 0 &&
                        new Date(detailedMetrics.metricsHistory[detailedMetrics.metricsHistory.length - 1].timestamp).toLocaleDateString()}
                    </Body2>
                  </div>
                </div>
              ) : (
                <Body1 style={{ color: tokens.colorNeutralForeground3 }}>No data available</Body1>
              )}
            </div>
          </Card>

          {/* Error Rate Chart Placeholder */}
          <Card>
            <CardHeader
              header={<Body1 style={{ fontWeight: 600 }}>Error Rate</Body1>}
              description={<Body2>API errors over time</Body2>}
            />
            <div style={{ padding: '1rem', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {detailedMetrics?.errorRateHistory && detailedMetrics.errorRateHistory.length > 0 ? (
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '150px' }}>
                    {detailedMetrics.errorRateHistory.slice(-30).map((point, idx) => {
                      const maxVal = Math.max(...detailedMetrics.errorRateHistory.map(p => p.value), 5);
                      const height = maxVal > 0 ? (point.value / maxVal) * 100 : 0;
                      return (
                        <div
                          key={idx}
                          style={{
                            flex: 1,
                            height: `${height}%`,
                            backgroundColor: point.value > 1 ? tokens.colorPaletteRedBackground2 : tokens.colorPaletteGreenBackground2,
                            borderRadius: '2px 2px 0 0',
                            minWidth: '4px',
                          }}
                          title={`${new Date(point.timestamp).toLocaleDateString()}: ${point.value.toFixed(2)}%`}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Body1 style={{ color: tokens.colorNeutralForeground3 }}>No error data available</Body1>
              )}
            </div>
          </Card>

          {/* Top Tenants */}
          <Card style={{ gridColumn: 'span 2' }}>
            <CardHeader
              header={<Body1 style={{ fontWeight: 600 }}>Most Active Tenants</Body1>}
              description={<Body2>Top 5 tenants by activity</Body2>}
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Tenant</TableHeaderCell>
                  <TableHeaderCell>Active Users</TableHeaderCell>
                  <TableHeaderCell>Scans (Month)</TableHeaderCell>
                  <TableHeaderCell>Open Findings</TableHeaderCell>
                  <TableHeaderCell>Last Activity</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailedMetrics?.topTenantsByActivity?.slice(0, 5).map((tenant: TenantActivityMetrics) => (
                  <TableRow key={tenant.tenantId}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <BuildingRegular />
                        <span style={{ fontWeight: 500 }}>{tenant.tenantName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{tenant.activeUsers}</TableCell>
                    <TableCell>{tenant.scansThisMonth}</TableCell>
                    <TableCell>
                      <Badge
                        appearance="tint"
                        color={tenant.findingsOpen > 100 ? 'danger' : tenant.findingsOpen > 50 ? 'warning' : 'success'}
                      >
                        {tenant.findingsOpen}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(tenant.lastActivity).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
                {(!detailedMetrics?.topTenantsByActivity || detailedMetrics.topTenantsByActivity.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>No tenant activity data</Body1>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {selectedTab === 'tenants' && (
        <Card>
          <CardHeader
            header={<Body1 style={{ fontWeight: 600 }}>All Tenant Activity</Body1>}
            description={<Body2>Activity metrics for all tenants</Body2>}
          />
          {rankingsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Spinner size="medium" label="Loading tenant data..." />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Rank</TableHeaderCell>
                  <TableHeaderCell>Tenant</TableHeaderCell>
                  <TableHeaderCell>Active Users</TableHeaderCell>
                  <TableHeaderCell>Scans (Month)</TableHeaderCell>
                  <TableHeaderCell>Open Findings</TableHeaderCell>
                  <TableHeaderCell>Last Activity</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantRankings?.map((tenant: TenantActivityMetrics, index: number) => (
                  <TableRow key={tenant.tenantId}>
                    <TableCell>
                      <Badge appearance={index < 3 ? 'filled' : 'outline'} color={index < 3 ? 'brand' : 'informative'}>
                        #{index + 1}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <BuildingRegular />
                        <span style={{ fontWeight: 500 }}>{tenant.tenantName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{tenant.activeUsers}</TableCell>
                    <TableCell>{tenant.scansThisMonth}</TableCell>
                    <TableCell>
                      <Badge
                        appearance="tint"
                        color={tenant.findingsOpen > 100 ? 'danger' : tenant.findingsOpen > 50 ? 'warning' : 'success'}
                      >
                        {tenant.findingsOpen}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(tenant.lastActivity).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
                {(!tenantRankings || tenantRankings.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <Body1>No tenant activity data available</Body1>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {selectedTab === 'api' && (
        <Card>
          <CardHeader
            image={<ServerRegular style={{ fontSize: '1.5rem' }} />}
            header={<Body1 style={{ fontWeight: 600 }}>API Endpoint Usage</Body1>}
            description={<Body2>Request counts and performance by endpoint</Body2>}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Endpoint</TableHeaderCell>
                <TableHeaderCell>Method</TableHeaderCell>
                <TableHeaderCell>Requests</TableHeaderCell>
                <TableHeaderCell>Avg Response Time</TableHeaderCell>
                <TableHeaderCell>Error Rate</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detailedMetrics?.apiUsageByEndpoint?.map((endpoint, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <code style={{ fontSize: '0.875rem' }}>{endpoint.endpoint}</code>
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance="outline"
                      color={
                        endpoint.method === 'GET' ? 'success' :
                        endpoint.method === 'POST' ? 'brand' :
                        endpoint.method === 'PUT' ? 'warning' :
                        endpoint.method === 'DELETE' ? 'danger' : 'informative'
                      }
                    >
                      {endpoint.method}
                    </Badge>
                  </TableCell>
                  <TableCell>{endpoint.requestCount.toLocaleString()}</TableCell>
                  <TableCell>
                    <span style={{
                      color: endpoint.avgResponseTimeMs < 200
                        ? tokens.colorPaletteGreenForeground1
                        : endpoint.avgResponseTimeMs < 500
                        ? tokens.colorPaletteYellowForeground1
                        : tokens.colorPaletteRedForeground1
                    }}>
                      {endpoint.avgResponseTimeMs}ms
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance="tint"
                      color={endpoint.errorRate < 1 ? 'success' : endpoint.errorRate < 5 ? 'warning' : 'danger'}
                    >
                      {endpoint.errorRate.toFixed(2)}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {(!detailedMetrics?.apiUsageByEndpoint || detailedMetrics.apiUsageByEndpoint.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Body1>No API usage data available</Body1>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};
